import { useMemo } from 'react';
import type { CellStyle, ColDef } from 'ag-grid-community';
import ShipmentOverflowTooltip from './ShipmentOverflowTooltip';
import type {
  ShipmentCellValue,
  ShipmentHeader,
  ShipmentRow,
} from '../../types';
import type {
  ShipmentAccordionCellProps,
  ShipmentVirtualTableProps,
} from './ShipmentVirtualTable.types';
import {
  ACTION_COLUMN_WIDTH,
  RICH_CELL_KEYS,
  SENSOR_COLUMN_VITAL_TYPES,
} from './shipmentTableConstants';
import ShipmentTablePlainCell from './ShipmentTablePlainCell';
import ShipmentSensorCell from './ShipmentSensorCell';
import ShipmentTableSkeletonCell from './ShipmentTableSkeletonCell';
import { ShipmentAlertsCell } from './AlertActivity';
import {
  isElSubRow,
  shouldShowExecutionLegAccordion,
} from './shipmentAccordionUtils';
import {
  renderActionCell,
  renderLocationCell,
  renderProductsCell,
  renderReferenceCell,
  renderTeamsCell,
  renderTrackingCell,
} from './ShipmentTableCells';
import { useShipmentPermissions } from '../../hooks/useShipmentPermissions';
import {
  formatCarrierName,
  formatValue,
  getColumnWidth,
  getShipmentRowId,
  isPlaceholderRow,
} from './shipmentTableUtils';
import useUserDateTimeFormat from '@/hooks/useUserDateTimeFormat';

interface UseShipmentTableColumnsProps {
  checked?: boolean;
  effectiveHeaderList: ShipmentHeader[];
  activeLegType?: 'CL' | 'EL';
  customerLegEnabled?: boolean;
  excludedIds: string[];
  getRowBackground: (record: ShipmentRow) => string | undefined;
  isSelectAllMode: boolean;
  onSelectChange: ShipmentVirtualTableProps['onSelectChange'];
  onShipmentAction?: ShipmentVirtualTableProps['onShipmentAction'];
  selectedIds: string[];
  shouldHideActionColumn: boolean;
  stickyColumnsEnabled: boolean;
  allPermissions?: {
    is_view?: boolean;
    is_update?: boolean;
    [key: string]: unknown;
  };
  shouldUseFullWidth?: boolean;
  drawerFourColumnMode?: boolean;
  drawerWideTrackingColumn?: boolean;
  isAccordionExpanded?: ShipmentAccordionCellProps['isAccordionExpanded'];
  onAccordionToggle?: ShipmentAccordionCellProps['onAccordionToggle'];
}

const ShipmentTableHeaderCell = ({ displayName }: { displayName?: string }) => (
  <ShipmentOverflowTooltip
    text={displayName || ''}
    className='shipment-grid-header-label'
  />
);

export const useShipmentTableColumns = ({
  checked,
  effectiveHeaderList,
  activeLegType,
  customerLegEnabled = false,
  excludedIds,
  getRowBackground,
  isSelectAllMode,
  onSelectChange,
  onShipmentAction,
  selectedIds,
  shouldHideActionColumn,
  stickyColumnsEnabled,
  allPermissions,
  shouldUseFullWidth,
  drawerFourColumnMode,
  drawerWideTrackingColumn,
  isAccordionExpanded,
  onAccordionToggle,
}: UseShipmentTableColumnsProps): ColDef<ShipmentRow>[] => {
  const { hasOperationalLegView } = useShipmentPermissions();
  const { dateFormat } = useUserDateTimeFormat();

  return useMemo(() => {
    const enabledHeaders = effectiveHeaderList.filter(
      (header) => header.enable !== false
    );
    const visibleHeaders = drawerFourColumnMode
      ? enabledHeaders.slice(0, 4)
      : enabledHeaders;

    const dataColumns: ColDef<ShipmentRow>[] = visibleHeaders.map(
      (header, index) => {
        const isFirstColumn = index === 0;
        const isRichCell = isFirstColumn || RICH_CELL_KEYS.has(header.key);
        const columnWidth = getColumnWidth(header.key);
        const useFullWidthColumn = shouldUseFullWidth && !drawerFourColumnMode;
        const shouldPinFirstColumn =
          stickyColumnsEnabled && isFirstColumn && !drawerFourColumnMode;
        const shouldFlexColumn = drawerFourColumnMode || useFullWidthColumn;
        const drawerColumnFlex =
          drawerWideTrackingColumn && header.key === 'custom_status' ? 1.6 : 1;
        const column: ColDef<ShipmentRow> = {
          headerName: header.value,
          headerComponent: ShipmentTableHeaderCell,
          field: header.key,
          colId: header.key,
          width: shouldFlexColumn ? undefined : columnWidth,
          flex: shouldFlexColumn ? drawerColumnFlex : undefined,
          minWidth: drawerFourColumnMode
            ? 0
            : useFullWidthColumn && header.key !== 'custom_status'
              ? 150
              : columnWidth,
          pinned: shouldPinFirstColumn ? 'left' : null,
          lockPinned: shouldPinFirstColumn,
          sortable: false,
          resizable: !shouldFlexColumn,
          suppressMovable: false,
          valueFormatter: (params) => {
            if (!params.data || isPlaceholderRow(params.data)) return '';
            if (header.key === 'courier_name') {
              return formatCarrierName(params.value);
            }
            return formatValue(params.value as ShipmentCellValue);
          },
        };

        if (shouldPinFirstColumn) {
          column.cellStyle = (params): CellStyle => ({
            background: params.data
              ? (getRowBackground(params.data) ?? '')
              : '',
          });
        }

        if (!isRichCell) {
          column.cellRendererSelector = (params) => {
            if (!params.data) return;

            if (isPlaceholderRow(params.data)) {
              return {
                component: ShipmentTableSkeletonCell,
                params: { columnIndex: index },
              };
            }

            // Accordion EL rows only use the first four customer-leg columns.
            if (isElSubRow(params.data) && index > 3) {
              return {
                component: ShipmentTablePlainCell,
                params: { value: '' },
              };
            }

            if (SENSOR_COLUMN_VITAL_TYPES[header.key]) {
              return {
                component: ShipmentSensorCell,
                params: {
                  value: params.value,
                  row: params.data,
                  columnKey: header.key,
                },
              };
            }

            return {
              component: ShipmentTablePlainCell,
              params: {
                value:
                  header.key === 'courier_name'
                    ? formatCarrierName(params.value)
                    : formatValue(params.value as ShipmentCellValue),
              },
            };
          };

          return column;
        }

        const pinnedCellStyle = column.cellStyle;
        column.cellStyle = (params) => ({
          ...(typeof pinnedCellStyle === 'function'
            ? pinnedCellStyle(params)
            : {}),
          overflow: 'hidden',
          display: 'flex',
          alignItems: 'center',
        });
        column.cellRenderer = (params: {
          value: ShipmentCellValue;
          data: ShipmentRow;
        }) => {
          const value = params.value;
          const record = params.data;
          if (!record) return null;

          if (isPlaceholderRow(record)) {
            return <ShipmentTableSkeletonCell columnIndex={index} />;
          }

          if (isElSubRow(record) && index > 3) {
            return null;
          }

          const rowId = getShipmentRowId(record);
          const selected = isSelectAllMode
            ? !excludedIds.includes(rowId)
            : selectedIds.includes(rowId);

          if (header.key === 'reference_number' || isFirstColumn) {
            return renderReferenceCell(
              record,
              selected,
              checked,
              onSelectChange,
              drawerFourColumnMode,
              activeLegType
            );
          }

          if (header.key === 'custom_status') {
            return renderTrackingCell(record, {
              showExecutionLegs: shouldShowExecutionLegAccordion(
                activeLegType,
                record,
                customerLegEnabled
              ),
              isExpanded: isAccordionExpanded?.(rowId) ?? false,
              customerLegEnabled,
              hasOperationalLegView,
              dateFormat,
              onToggle: onAccordionToggle
                ? () => onAccordionToggle(record)
                : undefined,
            });
          }

          if (header.key === 'from_location' || header.key === 'to_location') {
            return renderLocationCell(record, header.key, drawerFourColumnMode);
          }

          if (header.key === 'products') {
            return renderProductsCell(record);
          }

          if (header.key === 'teams') {
            return renderTeamsCell(record);
          }

          if (header.key === 'alert_count') {
            return (
              <ShipmentAlertsCell
                record={record}
                fallbackText={formatValue(value)}
              />
            );
          }

          return header.key === 'courier_name'
            ? formatCarrierName(value)
            : formatValue(value);
        };

        return column;
      }
    );

    if (dataColumns.length === 0 || shouldHideActionColumn) {
      return dataColumns;
    }

    if (drawerFourColumnMode) {
      return dataColumns;
    }

    const actionColumn: ColDef<ShipmentRow> = {
      headerName: '',
      colId: 'action',
      width:
        shouldUseFullWidth && !drawerFourColumnMode
          ? undefined
          : ACTION_COLUMN_WIDTH,
      flex: shouldUseFullWidth && !drawerFourColumnMode ? 0 : undefined,
      minWidth:
        shouldUseFullWidth && !drawerFourColumnMode
          ? ACTION_COLUMN_WIDTH
          : ACTION_COLUMN_WIDTH,
      maxWidth:
        shouldUseFullWidth && !drawerFourColumnMode
          ? ACTION_COLUMN_WIDTH
          : undefined,
      pinned: stickyColumnsEnabled && !drawerFourColumnMode ? 'right' : null,
      lockPinned: stickyColumnsEnabled && !drawerFourColumnMode,
      sortable: false,
      resizable: false,
      suppressMovable: true,
      cellStyle: (params): CellStyle => ({
        background: params.data ? (getRowBackground(params.data) ?? '') : '',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'flex-end',
      }),
      cellRenderer: (params: { data: ShipmentRow }) => {
        const record = params.data;
        if (!record || isPlaceholderRow(record)) return null;
        if (isElSubRow(record)) return null;

        return renderActionCell(
          record,
          onShipmentAction,
          allPermissions,
          activeLegType
        );
      },
    };

    return [...dataColumns, actionColumn];
  }, [
    checked,
    effectiveHeaderList,
    activeLegType,
    customerLegEnabled,
    excludedIds,
    getRowBackground,
    isSelectAllMode,
    onSelectChange,
    onShipmentAction,
    selectedIds,
    shouldHideActionColumn,
    stickyColumnsEnabled,
    allPermissions,
    shouldUseFullWidth,
    drawerFourColumnMode,
    drawerWideTrackingColumn,
    hasOperationalLegView,
    isAccordionExpanded,
    onAccordionToggle,
    dateFormat,
  ]);
};
